import apiService from 'services/api-service'
import { getLastSeenSellerRatingDate } from 'util/turnto-utils'
import { hostsConfig } from 'init/config'
import globalConfig from 'init/global.config'
import globalStore from 'init/global-store'
import { DoMoreProps } from 'components/do-more/do-more'
import log from 'util/logger'
import { logoutUser } from 'services/token-service'
import { TurnToFlow } from './email-flow-entry'
import msg from 'services/msg'
import { ssoGetLoggedInData } from 'services/sso-service'
import standalonePopoverLoader from 'widgets/standalone-popover/standalone-popover-loader'
import submissionStore, { LaunchData, Source, SubmissionPageName, SubmissionPage }
  from 'store/submission-store'
import submissionLoader from 'widgets/submission/submission-loader'
import { waitForValue } from 'util/ts-utils'

export function checkForFlow(
  flow: TurnToFlow,
  params: { [name: string]: string }
): Promise<boolean> {
  return new Promise((resolve) => {
    if (!isReviewFlow(flow, params)) {
      resolve(false)
      return
    }

    if (flow === 'optout') {
      standalonePopoverLoader.load({
        message: msg('flowMsg.optout.rse')
      })
      resolve(true)
      return
    }

    let page = 'review-form' as SubmissionPageName
    let launchData = {} as LaunchData
    let props = {}
    if (flow === 'review') {
      launchData = getRseLaunchData(params)
      if (launchData.startAction === 'visual') {
        page = 'share-media'
      }
    } else if (flow === 'rebelmail') {
      launchData = getRebelmailLaunchData(params)
      if (launchData.reviewId) { // was successfully submitted
        page = 'do-more'
        props = { msgType: 'review' } as DoMoreProps
      } else if (launchData.alreadyReviewed) {
        page = 'do-more'
        props = { msgType: 'alreadyReviewed' } as DoMoreProps
      }
    } else if (flow === 'revpr') {
      // coming from a purchase required confirmation email and should already be logged in from
      // email-flow-entry. now place the user on the review page
      launchData.startAction = 'review'
    }
    // if we have a RSE token, need to remove currently logged in user (if any)
    if (launchData.rseToken) {
      logoutUser()
    }
    // for successful inbox submission flow, need to check for seller rating eligibility.  Since
    // need to make server call, needs to be async and wait before proceeding.
    // Ideally would do with await/async, but not available in all browsers, so we have this nested
    // promises situation
    checkSellerRatingEligibile(launchData, params).then((transactionId) => {
      if (transactionId) {
        // because we are coming from an RSE, the token needs to be stored in review launch data
        // so the seller rating form knows which initData method to call
        submissionStore.launchData = { rseToken: launchData.rseToken }
        submissionStore.startSellerRatingFlow(
          hostsConfig.siteKey,
          { page: 'seller-rating-form', props: { msgType: 'reviewed' } },
          { transactionId }
        )
        submissionLoader.load().then(() => resolve(true))
        return
      }
      startRevFlow(flow, launchData, params, { page, props }).then(() => resolve(true))
    }).catch((e) => {
      log.error(e)
      startRevFlow(flow, launchData, params, { page, props }).then(() => resolve(true))
    })
  })
}

function startRevFlow(
  flow: TurnToFlow,
  launchData: LaunchData,
  params: { [name: string]: string },
  page: SubmissionPage
): Promise<boolean> {
  return new Promise((resolve, reject) => {
    waitForValue(() => params.turntosku || globalStore.sku).then((sku: string) => {
      if (globalConfig.ssoEnabled && ['review', 'rebelmail', 'share-media'].indexOf(flow) > -1) {
        ssoGetLoggedInData(sku, page, launchData, 'rev')
        resolve()
        return
      }
      submissionStore.startRevFlow(sku, hostsConfig.siteKey, page, launchData)
      submissionLoader.load().then(() => resolve())
    })
  })
}
// if yes, then resolves with transaction id, otherwise resolves to no
function checkSellerRatingEligibile(
  launchData: LaunchData,
  params: { [name: string]: string }
): Promise<number> {
  return new Promise((resolve, reject) => {
    if (launchData.reviewId && params.turntoCheckSrEligibility === '1') {
      apiService.checkSellerRatingEligibile(
        { 'context-token': launchData.rseToken, lastSeenSrDate: getLastSeenSellerRatingDate() }
      ).then((data) => {
        resolve(data.transactionId)
      }).catch((e) => {
        log.error(e)
        resolve(null)
      })
    } else {
      resolve(null)
    }
  })
}

function getRseLaunchData(params: { [name: string]: string }) {
  const launchData = {
    rseToken: params.tttok || params.token,
    source: Source.RSE
  } as LaunchData
  if (params.reviewStartAction) {
    launchData.startAction = params.reviewStartAction === 'shareAPhoto' ? 'visual' : 'review'
  }
  if (params.starSelected) {
    launchData.starSelected = parseInt(params.starSelected) || 0
    if (launchData.starSelected < 0 || launchData.starSelected > 5) {
      launchData.starSelected = 0
    }
  }
  return launchData
}

function getRebelmailLaunchData(params: { [name: string]: string }) {
  return {
    alreadyReviewed: params.turntotoAlreadyReviewed === '1',
    hasErrors: params.turntoReviewHasErrors ? true : false,
    showGuidelines: params.turntoShowGuidelines === '1' ? true : false,
    startAction: 'review',
    rseToken: params.tttok || params.token,
    reviewId: params.turntoReviewId ? parseInt(params.turntoReviewId) : null,
    source: Source.RSE
  } as LaunchData
}

function isReviewFlow(flow: TurnToFlow, params: { [name: string]: string }) {
   // If flow is 'optout', need to make sure it's from an RSE. Could be from question email.
  return (flow === 'optout' && (params.turntoEmailType === 'reviewSolicitation' ||
    params.turntoEmailType === 'rseFollowUp')) ||
    ['review', 'rebelmail', 'revpr'].indexOf(flow) > -1
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!(webpack)-strip-block!./src/email-flows/review-flows.ts